// NIGHT-DIRECTSTAFF-2026-10-22 §1: placements -- a traveler on a ProTherapy contract. The table is the agents window's
// (the sites repo's tools/sql/2026-10-22-placements.sql, filed for the hub): id uuid, candidate_id, facility, discipline,
// state, start_date, end_date, status (offered / confirmed / started / ending / ended / extended), source, created_by,
// package_note (free text, <= 2000), placement_outcome (null | leaving_travel | other_agency; the agent writes it), outcome_at.
// Until the hub applies it the CRM probes once and keeps Placements dark ("turns on with the update").
//
// The re-placement agent (recruiter-agent, REPLACEMENT_MODE log until real rows exist) contacts started / ending /
// extended rows with no outcome, at week 9 of a 13-week contract (84-98 days in) or 4 weeks before end_date; changing
// end_date starts a fresh flow (keyed <placement id>@<end_date>). So an extension is an end_date change, and its history
// is ours to keep: every end-date or status change writes one activity_log row on the candidate (type 'placement'),
// "Placement <id8> · <facility> · end date <from> → <to> · by <staff>". Nothing is deleted.

export const PLACEMENT_STATUSES = { offered: 'Offered', confirmed: 'Confirmed', started: 'Started', ending: 'Ending', ended: 'Ended', extended: 'Extended' };
export const ON_CONTRACT = new Set(['started', 'ending', 'extended']);
export const OUTCOMES = { leaving_travel: 'Leaving travel', other_agency: 'Going with another agency' };
export const NOTE_MAX = 2000;
export const FACILITY_MAX = 200;
export const PLACEMENT_LOG_TYPE = 'placement';
export const CONTRACT_WEEKS = 13;
export const COLS = 'id,candidate_id,facility,discipline,state,start_date,end_date,status,source,created_by,package_note,placement_outcome,outcome_at,created_at,updated_at';
export const DARK_WHY = 'Placements turn on when the hub applies the placements update (2026-10-22-placements.sql).';

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const dayMs = 864e5;
const toDate = (d) => (DATE.test(s(d)) ? new Date(`${s(d)}T12:00:00Z`) : null);
export const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);
// DAY-DIRECTSTAFF-2026-10-25: today is Matt's calendar day (Eastern), as the rest of Today counts it -- the UTC date turned a start
// date of today into "yesterday" every evening after 8 PM ET
export const today = (now = Date.now()) => new Date(now).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

/** The default end: start + 13 weeks (the contract length the agent times its opener by). '' without a start. */
export function defaultEnd(start) {
  const d = toDate(start);
  return d ? isoDay(d.getTime() + CONTRACT_WEEKS * 7 * dayMs) : '';
}
/** Whole days from today to the date (negative = past). null without a date. */
export function daysTo(date, now = Date.now()) {
  const d = toDate(date);
  return d ? Math.round((d.getTime() - toDate(today(now)).getTime()) / dayMs) : null;
}
export const fmtDay = (d) => { const x = toDate(d); return x ? x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : ''; };

// NIGHT-DIRECTSTAFF-2026-10-25c §3: Extend and Ending early, on the columns the placements table already has (nothing to apply):
//   Extend         a new end date after the current one -> status Extended, the end date, a package-note line "Extended to <date>:
//                  <note>"; the change history (and the timeline) keeps the old end date as every edit does
//   Ending early   a date (from the start to the current end) and a reason -> the end date, a package-note line "Ending early
//                  <date>: <reason>"; status Ending while that date is ahead -- the re-placement agent already contacts Ending rows
//                  in the 4 weeks before end_date, so the early date brings it forward -- and Ended once it is today or past
export const EARLY_REASONS = [['facility_cancelled', 'The facility cancelled the contract'], ['candidate_left', 'The candidate left early'],
  ['not_a_fit', 'Not a fit for the unit'], ['personal', 'Personal or family reasons'], ['other', 'Another reason']];
const noteWith = (old, line) => (s(old) ? `${s(old)}\n${line}` : line).slice(0, NOTE_MAX);
/** -> { form, error } : the patch Extend makes, or why it cannot */
export function extendForm(p, newEnd, note = '') {
  if (!DATE.test(s(newEnd))) return { error: 'Choose the new end date.' };
  if (p.end_date && s(newEnd) <= s(p.end_date)) return { error: 'The new end date must be after the current one.' };
  const line = `Extended to ${fmtDay(newEnd)}${s(note) ? `: ${s(note).replace(/\s+/g, ' ').slice(0, 200)}` : ''}`;
  return { form: { end_date: newEnd, status: 'extended', package_note: noteWith(p.package_note, line) } };
}
/** -> { form, error } : the patch Ending early makes */
export function endEarlyForm(p, date, reason, now = Date.now()) {
  if (!DATE.test(s(date))) return { error: 'Choose the last day.' };
  if (p.start_date && s(date) < s(p.start_date)) return { error: 'The last day cannot be before the start.' };
  if (p.end_date && s(date) > s(p.end_date)) return { error: 'An early end must be before the current end date. To go later, extend it.' };
  const r = EARLY_REASONS.find(([k]) => k === reason);
  if (!r) return { error: 'Choose a reason.' };
  const status = s(date) > today(now) ? 'ending' : 'ended';
  return { form: { end_date: date, status, package_note: noteWith(p.package_note, `Ending early ${fmtDay(date)}: ${r[1]}`) } };
}

/** Form -> { row, errors }. errors keyed by field, plain words. */
export function validatePlacement(f) {
  const errors = {};
  const row = {
    facility: s(f.facility), discipline: s(f.discipline).toUpperCase(), state: s(f.state).toUpperCase(),
    start_date: s(f.start_date) || null, end_date: s(f.end_date) || null, status: s(f.status) || 'offered', package_note: s(f.package_note) || null,
  };
  if (!row.facility) errors.facility = 'Which facility?';
  else if (row.facility.length > FACILITY_MAX) errors.facility = `The facility is too long (${FACILITY_MAX} characters at most).`;
  if (row.state && !/^[A-Z]{2}$/.test(row.state)) errors.state = 'Two letters, like TX.';
  if (row.discipline.length > 20) errors.discipline = 'Too long.';
  if (row.start_date && !toDate(row.start_date)) errors.start_date = 'Not a date.';
  if (row.end_date && !toDate(row.end_date)) errors.end_date = 'Not a date.';
  if (!errors.start_date && !errors.end_date && row.start_date && row.end_date && row.end_date < row.start_date) errors.end_date = 'The end is before the start.';
  if (!PLACEMENT_STATUSES[row.status]) errors.status = 'Pick a status.';
  if (row.package_note && row.package_note.length > NOTE_MAX) errors.package_note = `Too long (${NOTE_MAX} characters at most).`;
  if (!row.discipline) row.discipline = null;
  if (!row.state) row.state = null;
  return { row, errors };
}

/** Is the agent able to contact this one? (on contract, no outcome) */
export const contactable = (p) => !!p && ON_CONTRACT.has(s(p.status)) && !p.placement_outcome;

/** The candidate's placement to show in the header: on contract and not yet over, the latest end first; else null. */
export function currentPlacement(rows = [], now = Date.now()) {
  const t = today(now);
  const on = rows.filter((p) => ON_CONTRACT.has(s(p.status)) && (!p.end_date || p.end_date >= t));
  on.sort((a, b) => s(b.end_date || '9999').localeCompare(s(a.end_date || '9999')));
  return on[0] || null;
}
/** "At <facility> until <date>" (or "At <facility>" with no end date yet). */
export function headerLine(p) {
  if (!p) return '';
  return p.end_date ? `At ${s(p.facility) || 'a facility'} until ${fmtDay(p.end_date)}` : `At ${s(p.facility) || 'a facility'}`;
}

/** The history note for a change (null when nothing tracked changed). Never a pay figure or a person's details. */
export function changeNote(p, patch, by = '') {
  const id8 = s(p.id).slice(0, 8);
  const parts = [];
  if ('end_date' in patch && s(patch.end_date) !== s(p.end_date)) parts.push(`end date ${s(p.end_date) || 'none'} → ${s(patch.end_date) || 'none'}`);
  if ('status' in patch && s(patch.status) !== s(p.status)) parts.push(`status ${PLACEMENT_STATUSES[s(p.status)] || s(p.status)} → ${PLACEMENT_STATUSES[s(patch.status)] || s(patch.status)}`);
  if (!parts.length) return null;
  return `Placement ${id8} · ${s(patch.facility || p.facility) || 'facility'} · ${parts.join(' · ')}${s(by) ? ` · by ${s(by)}` : ''}`;
}
const NOTE_RE = /^Placement ([0-9a-f]{8}) · (.+?) · (.+?)(?: · by (.+))?$/;
export function parseChangeNote(note) {
  const m = NOTE_RE.exec(s(note));
  if (!m) return null;
  const rest = m[3].split(' · ');
  return { id8: m[1], facility: m[2], changes: rest.filter((x) => /^(end date|status) /.test(x)), by: m[4] || '' };
}

/** Timeline entries: each placement's start / end (if started or ended), and every logged change. */
export function placementEntries(placements = [], activity = [], now = Date.now()) {
  const out = [];
  const t = today(now);
  for (const p of placements) {
    const name = s(p.facility) || 'a facility';
    out.push({ at: p.created_at, kind: 'placement', who: 'Placement added', line: `${name}${p.start_date ? ` · ${fmtDay(p.start_date)}` : ''}${p.end_date ? ` → ${fmtDay(p.end_date)}` : ''}`, full: '' });
    if (p.start_date && p.start_date <= t && ['started', 'ending', 'extended', 'ended'].includes(s(p.status))) out.push({ at: `${p.start_date}T12:00:00.000Z`, kind: 'placement', who: 'Started', line: name, full: '' });
    if (p.end_date && s(p.status) === 'ended') out.push({ at: `${p.end_date}T12:00:00.000Z`, kind: 'placement', who: 'Ended', line: name, full: '' });
    if (p.placement_outcome && p.outcome_at) out.push({ at: p.outcome_at, kind: 'placement', who: 'After this contract', line: `${OUTCOMES[p.placement_outcome] || p.placement_outcome} · ${name}`, full: '', tone: 'bad' });
  }
  for (const a of activity) {
    if (s(a.type) !== PLACEMENT_LOG_TYPE) continue;
    const m = parseChangeNote(a.note);
    if (!m) continue;
    const ext = m.changes.some((c) => /^end date /.test(c));
    out.push({ at: a.created_at || a.date, kind: 'placement', who: ext ? `Extended / end date changed · ${m.facility}` : `Placement · ${m.facility}`, line: m.changes.join(' · ') + (m.by ? ` · by ${m.by}` : ''), full: '' });
  }
  return out.filter((e) => e.at && !Number.isNaN(new Date(e.at).getTime()));
}

/** Is the table there? (the hub applies the agents window's SQL) */
export async function probePlacements(client) {
  try { const { error } = await client.from('placements').select('id', { head: true }).limit(0); return !error; } catch { return false; }
}

/**
 * supabase -> the placements reads and writes.
 *   list(candidateId?) -> rows (newest end first);  add(form, by) -> row;  update(p, patch, by) -> row (logs end-date / status changes)
 */
export function makePlacementsApi({ supabase }) {
  const whoAmI = async (by) => {
    if (s(by)) return s(by);
    try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.email) || ''; } catch { return ''; }
  };
  return {
    async list(candidateId = null) {
      let q = supabase.from('placements').select(COLS);
      if (candidateId !== null && candidateId !== undefined) q = q.eq('candidate_id', candidateId);
      const { data, error } = await q.order('end_date', { ascending: false });
      if (error) throw new Error(error.message || 'Placements could not be read.');
      return data || [];
    },
    async add(candidateId, form, by = '') {
      const { row, errors } = validatePlacement(form);
      if (Object.keys(errors).length) { const e = new Error(Object.values(errors)[0]); e.errors = errors; throw e; }
      const who = await whoAmI(by);
      const { data, error } = await supabase.from('placements').insert({ ...row, candidate_id: candidateId, source: 'crm', created_by: who || null }).select(COLS);
      if (error) throw new Error(error.message || 'Not saved.');
      return (data || [])[0];
    },
    async update(p, form, by = '') {
      const { row, errors } = validatePlacement({ ...p, ...form });
      if (Object.keys(errors).length) { const e = new Error(Object.values(errors)[0]); e.errors = errors; throw e; }
      const patch = {};
      for (const k of ['facility', 'discipline', 'state', 'start_date', 'end_date', 'status', 'package_note']) if (s(row[k]) !== s(p[k])) patch[k] = row[k];
      if (!Object.keys(patch).length) return p;
      patch.updated_at = new Date().toISOString();
      const { data, error } = await supabase.from('placements').update(patch).eq('id', p.id).select(COLS);
      if (error) throw new Error(error.message || 'Not saved.');
      const who = await whoAmI(by);
      const note = changeNote(p, patch, who);
      if (note) await supabase.from('activity_log').insert({ candidate_id: p.candidate_id, date: today(), type: PLACEMENT_LOG_TYPE, note });
      return (data || [])[0] || { ...p, ...patch };
    },
  };
}
